import { enAR, hoyAr } from "./fechaAr";
import { esPersonal, patenteBusqueda } from "./autosAutorizados";

/* Registro de Salida Pendiente (Guillermo, Portería — ticket 417, 15/10).
 *
 * «Los vehículos autorizados que se cargaron corresponden a personal de los
 * sectores, quienes cumplen un horario fijo de lunes a jueves […] Quedan
 * excluidos los viernes, sábados, domingos y feriados. […] Se deberá tomar
 * como franja horaria de las 06 a las 18 […] superado ese horario máximo de
 * las 18 se deberá activar la notificación».
 *
 * ── EL HORARIO ES UNO PARA TODOS ──────────────────────────────────────────
 *
 * Cada auto del personal no tiene un horario cargado (la lista guarda patente,
 * nombre, sector y DNI). Por eso la regla es la de la franja que pidió
 * Guillermo: HORA_MAXIMA = 18:00 para todos. El día que el horario se cargue
 * por persona, lo que cambia es `HORA_MAXIMA` por un dato de la fila.
 *
 * ── LOS TIEMPOS ───────────────────────────────────────────────────────────
 *
 * «La notificación deberá aparecer 30 minutos después del horario máximo y
 * cada 5 minutos por el lapso de 30 minutos más»: 18:30, 18:35 … 19:00 (siete
 * toques). Pasadas las 19:00 sin completar queda la campana. A las 23:59 los
 * pendientes del día se van solos: son las entradas de HOY sin salida, y
 * mañana es otro día — no hace falta una tarea que los borre. */

export const HORA_MAXIMA = 18 * 60; // en minutos del día
export const ESPERA = 30;
export const CADA = 5;
export const LAPSO = 30;

export const PRIMER_TOQUE = HORA_MAXIMA + ESPERA; // 18:30
export const ULTIMO_TOQUE = PRIMER_TOQUE + LAPSO; // 19:00

/* Feriados nacionales en días de lunes a jueves (los demás no cuentan igual).
 *
 * No había una lista en el repo: ésta es la nacional, con los trasladables ya
 * movidos (ley 27.399) y los días no laborables con fines turísticos de 2026.
 * Los de 2027 que dependen de un decreto (puentes) se suman cuando salga.
 * Si la empresa cierra otro día, se agrega acá. */
export const FERIADOS = new Set([
  // 2026
  "2026-01-01", // Año nuevo
  "2026-02-16", // Carnaval
  "2026-02-17", // Carnaval
  "2026-03-23", // Puente turístico
  "2026-03-24", // Memoria
  "2026-04-02", // Malvinas / Jueves Santo
  "2026-04-03", // Viernes Santo
  "2026-05-01", // Trabajador
  "2026-05-25", // Revolución de Mayo
  "2026-06-15", // Güemes (trasladado del 17)
  "2026-06-20", // Belgrano
  "2026-07-09", // Independencia
  "2026-07-10", // Puente turístico
  "2026-08-17", // San Martín
  "2026-10-12", // Diversidad cultural
  "2026-11-23", // Soberanía (trasladado del 20)
  "2026-12-07", // Puente turístico
  "2026-12-08", // Inmaculada Concepción
  "2026-12-25", // Navidad
  // 2027
  "2027-01-01",
  "2027-02-08", // Carnaval
  "2027-02-09", // Carnaval
  "2027-03-24", // Memoria
  "2027-03-25", // Jueves Santo
  "2027-03-26", // Viernes Santo
  "2027-04-02", // Malvinas
  "2027-05-01",
  "2027-05-25",
  "2027-06-20",
  "2027-06-21", // Güemes (trasladado del 17)
  "2027-07-09",
  "2027-08-16", // San Martín (trasladado del 17)
  "2027-10-11", // Diversidad cultural (trasladado del 12)
  "2027-11-20",
  "2027-12-08",
  "2027-12-25",
]);

/* Día de la semana de un «YYYY-MM-DD»: 0 = domingo … 6 = sábado. Sin pasar
 * por la hora local de la máquina. */
const diaSemana = (dia) => new Date(`${dia}T12:00:00Z`).getUTCDay();

/* ¿Ese día se controla? Lunes a jueves, y que no sea feriado. */
export function esDiaDeControl(dia, feriados = FERIADOS) {
  const d = diaSemana(dia);
  return d >= 1 && d <= 4 && !feriados.has(dia);
}

/* Minutos del día en hora argentina. */
export function minutosAr(ahora = new Date()) {
  const d = enAR(ahora);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/* En qué parte del día estamos:
 *   · "nada"    — viernes a domingo o feriado: no se controla.
 *   · "antes"   — todavía no son las 18:30.
 *   · "avisos"  — de 18:30 a 19:00 inclusive: banner cada 5 minutos.
 *   · "agotado" — pasadas las 19:00: si sigue pendiente, a la campana. */
export function fase(ahora = new Date(), feriados = FERIADOS) {
  if (!esDiaDeControl(hoyAr(ahora), feriados)) return "nada";
  const m = minutosAr(ahora);
  if (m < PRIMER_TOQUE) return "antes";
  if (m <= ULTIMO_TOQUE) return "avisos";
  return "agotado";
}

/* El número de toque vigente (0 a las 18:30, 1 a las 18:35 … 6 a las 19:00),
 * o -1 fuera de la ventana. «Aceptar» guarda el toque que cerró; el banner
 * vuelve cuando llega uno más nuevo. */
export function toqueActual(ahora = new Date(), feriados = FERIADOS) {
  if (fase(ahora, feriados) !== "avisos") return -1;
  return Math.floor((minutosAr(ahora) - PRIMER_TOQUE) / CADA);
}

/* Los autos del personal que entraron HOY y siguen sin salida.
 *
 * `adentro` son las filas de `vigilancia_registros` sin salida; `autorizados`,
 * la lista vigente (los del personal traen `personal = true`). La entrada se
 * liga al auto por `autorizacion_id` o, si se registró sin ligar, por la
 * patente (comparada sólo por letras y números). Las de días anteriores no
 * cuentan: «a las 23:59 el sistema deberá borrar esos pendientes». */
export function pendientesDeSalida(adentro = [], autorizados = [], ahora = new Date()) {
  const hoy = hoyAr(ahora);
  const delPersonal = (autorizados || []).filter(esPersonal);
  const porId = new Map(delPersonal.map((a) => [String(a.id), a]));
  const porPatente = new Map(
    delPersonal.filter((a) => patenteBusqueda(a.patente)).map((a) => [patenteBusqueda(a.patente), a]),
  );
  const vistos = new Set();
  const salida = [];
  for (const r of adentro || []) {
    if (!r || r.salida || !r.entrada || hoyAr(new Date(r.entrada)) !== hoy) continue;
    const auto =
      (r.autorizacion_id != null && porId.get(String(r.autorizacion_id))) ||
      porPatente.get(patenteBusqueda(r.patente));
    if (!auto || vistos.has(r.id)) continue;
    vistos.add(r.id);
    salida.push({
      id: r.id,
      patente: r.patente || auto.patente || "",
      nombre: r.nombre || auto.nombre || "",
    });
  }
  return salida;
}

/* ¿A quién se le avisa? A la barrera: la llave `cargar.vigilancia`. Quien
 * además autoriza (`autorizar.vigilancia`: Nico, Marcos) no está en el portón
 * a las 18:30, y un banner cada cinco minutos ahí sería ruido. */
export function esDeLaBarrera(permisos) {
  if (!Array.isArray(permisos)) return false;
  return permisos.includes("cargar.vigilancia") && !permisos.includes("autorizar.vigilancia");
}
